import { cancelingSingleton } from '../backpressure/canceling'
import { makeRequest } from './requests'
import getLogger from '../debuggers/logger'
import customizeError from '../errors/custom'

const logger = getLogger('Supabase Services')

/**
 * Factory to create a wrapper to call services.
 *
 * Generate a wrapper with the following signature:
 *
 *  interface Args {
 *    actionType?: string;
 *    [key: string]: any;
 *  }
 *  interface Response {
 *    data: object;
 *    headers: object;
 *    request: object;
 *    status: number;
 *  }
 *
 *  type ServiceWrapper = (args: Args) => Promise<Response>
 *
 * E.g.
 *
 *  getService('https://api.example.com/auth', { id: 2, ... })
 */
const factoryService = (service) => {
  const canceling = cancelingSingleton()

  return (serviceArgs) => {
    const { actionType, ignoreCancelingError = false, ...requestArgs } = serviceArgs || {}
    const request = () => service(requestArgs)

    canceling.run(actionType)

    return makeRequest(request, (result) => {
      logger.debug('SERVICE', { ...serviceArgs, result })
    }).catch((error) => {
      if (ignoreCancelingError && (error.code === 'ERR_CANCELED' || error.code === 'ERR_ABORTED')) {
        return new Promise(() => undefined)
      }

      const serviceError = customizeError({ originalError: error, reason: 'SERVICE_ERROR' })
      logger.debug('SERVICE', { ...serviceArgs, error: serviceError })
      throw serviceError
    })
  }
}

export default factoryService
